import { Link } from '@tanstack/react-router';
import { useAssetLogo } from '@interxfi/api-client/asset/use-asset-logo';
import { useIxcAssetsV1AssetQueryServiceAssetGet } from '@interxfi/api-client/generated/hooks/ixc-assets-v1-asset-query-service-asset-get';
import { AssetLogoWithLabel } from '@interxfi/app-components/components/asset-logo/with-label/asset-logo-with-label';
import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNode,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CurrencyFormat } from '@interxfi/design-system/components/format/currency';
import { ID } from '@interxfi/design-system/components/format/id';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list';
import { defaultValueStackedTypographyProps } from '@interxfi/design-system/components/list/readonly/item/value';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import {
  getOneOfType,
  type IxcAssetsV1AssetData,
  type IxcAssetsV1AssetState,
  type IxcAssetsV1AssetType,
  type IxcAssetsV1AssetTypeCash,
  type IxcAssetsV1AssetTypeEquity,
  type IxcAssetsV1Controls,
  type IxcAssetsV1FundNav,
  type IxcAssetsV1MoneyMarket,
  type IxcAssetsV1MoneyMarketFundHours,
  type IxcAssetsV1NavStrike,
  type IxcAssetsV1Security,
  type IxcAssetsV1Symbol,
} from '@interxfi/types';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { formatTimeZoneNameForDisplay } from '../hours/helpers';
import { getJ5StateMetadataListItems } from '../j5/state/j5-state-list-items';
import { getAssetDetailPath } from './asset-routing';
import { AssetStatusPill } from './asset-status-pill';
import { formatAssetSymbol, getAssetName, getAssetSymbol } from './utils';

export function getAssetNavStrikeListItems(data: IxcAssetsV1NavStrike | undefined, prefix = '') {
  return {
    strikeTime: getReadonlyListItemString(`${prefix}strikeTime`, 'Strike Time', data?.strikeTime),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAssetNavStrikesSummaryListItem(data: IxcAssetsV1NavStrike[] | undefined, id = '') {
  const hasGt1Strike = (data?.length || 0) > 1;

  return getReadonlyListItemNode(
    id,
    'Strikes',
    data?.length ? (
      <VerticalStack gap={1}>
        {data?.map((strike) => (
          <span key={`${strike.amount}-${strike.strikeTime}`}>
            <CurrencyFormat value={strike.amount} withoutTypography />
            {strike.strikeTime ? ` (${strike.strikeTime})` : ''}
          </span>
        ))}
      </VerticalStack>
    ) : undefined,
    hasGt1Strike ? { columns: 1, valueTypography: { ...defaultValueStackedTypographyProps } } : undefined,
  );
}

export function getAssetFundNavListItems(data: IxcAssetsV1FundNav | undefined, prefix = '') {
  return {
    navDate: getReadonlyListItemDate(`${prefix}navDate`, 'NAV Date', data?.navDate, 'date_iso'),
    strikes: data?.strikes?.map((strike, index) => getAssetNavStrikeListItems(strike, `${prefix}strikes.${index}.`)),
    strikeSummary: getAssetNavStrikesSummaryListItem(data?.strikes, `${prefix}strikeSummary`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAssetSymbolListItems(data: IxcAssetsV1Symbol | undefined, prefix = '') {
  return {
    base: getReadonlyListItemString(`${prefix}base`, 'Base', data?.base),
    suffix: getReadonlyListItemTranslatedString(`${prefix}suffix`, 'Suffix', {
      namespace: 'ixcAssetsV1',
      enumName: 'IxcAssetsV1SymbolSuffix',
      value: data?.suffix,
    }),
    formatted: getReadonlyListItemString(`${prefix}formatted`, 'Symbol', formatAssetSymbol(data)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAssetSecurityListItems(data: IxcAssetsV1Security | undefined, prefix = '') {
  return {
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    symbol: getAssetSymbolListItems(data?.symbol, `${prefix}symbol`),
    name: getReadonlyListItemString(`${prefix}name`, 'Name', data?.name),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAssetControlsListItems(data: IxcAssetsV1Controls | undefined, prefix = '') {
  return {
    canBuy: getReadonlyListItemYesOrNo(`${prefix}canBuy`, 'Buy', Boolean(data?.canBuy)),
    canSell: getReadonlyListItemYesOrNo(`${prefix}canSell`, 'Sell', Boolean(data?.canSell)),
    canEnterFractions: getReadonlyListItemYesOrNo(`${prefix}canEnterFractions`, 'Enter Fractions', Boolean(data?.canEnterFractions)),
  };
}

export function getAssetsCashListItems(data: IxcAssetsV1AssetTypeCash | undefined, prefix = '') {
  return {
    currency: getReadonlyListItemTranslatedString(`${prefix}currency`, 'Currency', {
      namespace: 'isoV1',
      enumName: 'IsoV1Currency',
      value: data?.currency,
    }),
    homeCurrency: getReadonlyListItemYesOrNo(`${prefix}homeCurrency`, 'Home Currency', Boolean(data?.homeCurrency)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAssetsEquityListItems(data: IxcAssetsV1AssetTypeEquity | undefined, prefix = '') {
  return {
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    symbol: getAssetSymbolListItems(data?.symbol, `${prefix}symbol`),
    name: getReadonlyListItemString(`${prefix}name`, 'Name', data?.name),
    controls: getAssetControlsListItems(data?.controls, `${prefix}controls.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAssetsMoneyMarketFundHoursListItems(data: IxcAssetsV1MoneyMarketFundHours | undefined, prefix = '', label = 'Hours') {
  return {
    opens: getReadonlyListItemString(`${prefix}opens`, 'Opens', data?.opens),
    closes: getReadonlyListItemString(`${prefix}closes`, 'Closes', data?.closes),
    summary: getReadonlyListItemString(`${prefix}summary`, label, data?.opens && data?.closes ? [data.opens, data.closes].join(' - ') : undefined),
    timeZone: getReadonlyListItemString(`${prefix}timeZone`, 'Time Zone', data?.timezone ? formatTimeZoneNameForDisplay(data.timezone) : undefined),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAssetsMoneyMarketListItems(data: IxcAssetsV1MoneyMarket | undefined, prefix = '') {
  return {
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    symbol: getAssetSymbolListItems(data?.symbol, `${prefix}symbol`),
    name: getReadonlyListItemString(`${prefix}name`, 'Name', data?.name),
    fundFamily: getReadonlyListItemTranslatedString(`${prefix}fundFamily`, 'Fund Family', {
      namespace: 'ixcAssetsV1',
      enumName: 'IxcAssetsV1FundFamily',
      value: data?.fundFamily,
    }),
    fundHours: getAssetsMoneyMarketFundHoursListItems(data?.fundHours, `${prefix}fundHours.`, 'Fund Hours'),
    shareClassNumber: getReadonlyListItemString(`${prefix}shareClassNumber`, 'Share Class Number', data?.shareClassNumber),
    accrualPeriod: getReadonlyListItemTranslatedString(`${prefix}accrualPeriod`, 'Accrual Period', {
      namespace: 'ixcAssetsV1',
      enumName: 'IxcAssetsV1AccrualPeriod',
      value: data?.accrualPeriod,
    }),
    dividendPayableDate: getReadonlyListItemTranslatedString(`${prefix}dividendPayableDate`, 'Dividend Payable Date', {
      namespace: 'ixcAssetsV1',
      enumName: 'IxcAssetsV1DividendPayableDate',
      value: data?.dividendPayableDate,
    }),
    classification: getReadonlyListItemTranslatedString(`${prefix}classification`, 'Classification', {
      namespace: 'ixcAssetsV1',
      enumName: 'IxcAssetsV1MoneyMarketClassification',
      value: data?.classification,
    }),
    navType: getReadonlyListItemTranslatedString(`${prefix}navType`, 'NAV Type', {
      namespace: 'ixcAssetsV1',
      enumName: 'IxcAssetsV1MoneyMarketNavType',
      value: data?.navType,
    }),
    controls: getAssetControlsListItems(data?.controls, `${prefix}controls.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAssetTypeListItems(data: IxcAssetsV1AssetType | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcAssetsV1',
      oneOfName: 'IxcAssetsV1AssetType',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('cash' in data) {
    return {
      ...typeNameColumn,
      cash: {
        '!type': 'cash',
        value: getAssetsCashListItems(data.cash, `${prefix}cash.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('equity' in data) {
    return {
      ...typeNameColumn,
      equity: {
        '!type': 'equity',
        value: getAssetsEquityListItems(data.equity, `${prefix}equity.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('moneyMarket' in data) {
    return {
      ...typeNameColumn,
      moneyMarket: {
        '!type': 'moneyMarket',
        value: getAssetsMoneyMarketListItems(data.moneyMarket, `${prefix}moneyMarket.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getAssetDataListItems(data: IxcAssetsV1AssetData | undefined, prefix = '') {
  return {
    category: getReadonlyListItemTranslatedString(`${prefix}category`, 'Category', {
      namespace: 'ixcAssetsV1',
      enumName: 'IxcAssetsV1AssetCategory',
      value: data?.category,
    }),
    decimalPrecision: getReadonlyListItemNumber(`${prefix}decimalPrecision`, 'Decimal Precision', data?.decimalPrecision),
    fixedPrecision: getReadonlyListItemYesOrNo(`${prefix}fixedPrecision`, 'Fixed Precision', data?.fixedPrecision),
    type: getAssetTypeListItems(data?.type, `${prefix}type`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getAssetStateListItems(data: IxcAssetsV1AssetState | undefined, prefix = '') {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata, `${prefix}metadata.`),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId),
    status: getReadonlyListItemTranslatedString(`${prefix}status`, 'Status', {
      namespace: 'ixcAssetsV1',
      enumName: 'IxcAssetsV1AssetStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill(`${prefix}statusPill`, 'Status', data?.status ? <AssetStatusPill status={data.status} /> : null),
    data: getAssetDataListItems(data?.data, `${prefix}data.`),
    summary: !('cash' in (data?.data?.type || {}))
      ? getReadonlyListItemNode(
          `${prefix}assetSummary`,
          'Symbol',
          <AssetSummaryNode assetId={data?.assetId} symbol={getAssetSymbol(data?.data?.type)} to={getAssetDetailPath(data?.assetId)} />,
        )
      : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

function AssetSummaryNode({ assetId, symbol, to }: { assetId: string | undefined; symbol: string | undefined; to: string | undefined }) {
  const { logoSrc, isPending } = useAssetLogo(assetId);

  return (
    <AssetLogoWithLabel
      alt={symbol ? `${symbol} Logo` : undefined}
      isPending={isPending}
      LinkElement={Link}
      label={symbol}
      logoSrc={logoSrc}
      to={to}
    />
  );
}

const linkTypography = {
  wrap: 'ellipsis',
} as const;

export function AssetNameNode({ assetId }: { assetId: string | undefined }) {
  const { data, isPending } = useIxcAssetsV1AssetQueryServiceAssetGet(assetId ? { assetId } : undefined);

  if (isPending) {
    return <Skeleton charCount={20} />;
  }

  const linkTo = getAssetDetailPath(assetId);
  const name = getAssetName(data?.asset?.data?.type);

  if (name) {
    if (!linkTo) {
      return name;
    }

    return (
      <TextLink LinkElement={Link} to={linkTo} typography={linkTypography}>
        {name}
      </TextLink>
    );
  }

  return <ID canCopy={false} LinkElement={Link} short={false} to={linkTo} value={assetId} />;
}

export function getAssetNameNodeListItem(id: string, title: string, assetId: string | undefined) {
  return getReadonlyListItemNode(id, title, assetId ? <AssetNameNode assetId={assetId} /> : undefined);
}
